import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import VideoBox from "../../casinoComponents/videoBox";
import BetTableTeenPatti3 from "../../casinoComponents/betTableTeenPatti3";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import MyBet from "../../casinoComponents/myBet";
import Teen20RefCardsOverlay from "../../casinoComponents/videoBox/overlays/Teen20RefCardsOverlay";
import ViewMoreModal from "../../../components/ViewMoreModal";
import ResultTeen41 from "../resultui/resultTeen41/ResultTeen41";
import { getCasinoGameDetails, getMatchExposure, getMyBets } from "../../../apiservices/CasionApi";
import { extractCasinoGame } from "../../../utils/casinoGame";
import { formatTeen6Nation } from "../../../utils/casinoMyBets";
import "../../../styles/games/teen4142.css";

// Helper function to normalize string for matching
const normalizeString = (str) => {
  if (!str) return "";
  return str
    .replace(/\s*\(\s*/g, "(")
    .replace(/\s*\)\s*/g, ")")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
};

// Helper function to create all exposure key variations
const createExposureKeys = (selection) => {
  if (!selection) return [];
  const keys = [selection, selection.toLowerCase(), selection.toLowerCase().trim(), selection.trim(), normalizeString(selection)];
  return [...new Set(keys.filter((k) => k))];
};

const bySr = (a, b) => (Number(a?.sr) || 0) - (Number(b?.sr) || 0);

/**
 * Queen Top Open (teen41) / Jack Top Open (teen42) Teenpatti in the reference shell
 * (.casino-table.teenpatti2): game header, video with the A/B card overlay, Top Open bet table,
 * remark marquee, Last Result strip + result modal; My Bets in the right sidebar.
 */
export default function TeenTopOpenPage({ gameType, title }) {
  const [gameData, setGameData] = useState(null);
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);
  const { pathname } = useLocation();
  const iframeSrc = `https://stream-s-43.uhdmovies.online/casino-stream?id=${extractCasinoGame(pathname)}`;
  const matchId = gameData?.data?.data?.mid?.toString();

  useEffect(() => {
    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(gameType);
        setGameData(response);
      } catch (err) {
        console.error(err.message);
      }
    };

    fetchGameData();
    const gameDataInterval = setInterval(fetchGameData, 1500);
    return () => clearInterval(gameDataInterval);
  }, [pathname, gameType]);

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;
      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            const teamName = item.team_name || item.selection || item.player_name || "";
            const exposureAmount = parseFloat(item.exposure_amount) || 0;
            if (teamName && exposureAmount !== 0) {
              createExposureKeys(teamName).forEach((key) => {
                if (key) exposureMap[key] = exposureAmount;
              });
            }
          });
          setExposures(exposureMap); // replace, not merge: a settled / finished round's book must drop
        }
      }
    } catch (error) {
      console.error("Error fetching exposure:", error);
    }
  }, [matchId]);

  const fetchMyBetsData = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;
      if (userId && matchId) {
        const response = await getMyBets(userId, matchId);
        if (response.success && response.bets) {
          const formattedBets = response.bets.map((bet) => ({
            // My Bets "Gametype" column (casino_bets.game_name, e.g. mogambo)
            description: bet.description ?? bet.game_name ?? bet.gameName,
            userName: bet.userName ?? bet.username,
            placeDate: bet.placeDate ?? bet.created_at,
            matchedBet: bet.player_name || bet.selection || "",
            selection: bet.selection || bet.player_name || "",
            player_name: bet.player_name || "",
            odds: bet.odds || "0",
            stake: bet.stake || "0",
            type: bet.type || null,
            exposer: parseFloat(bet.exposer || bet.exposure_amount || "0") || 0,
          }));
          setMyBets(formattedBets);

          // Also update exposures from myBets
          const exposureMap = {};
          response.bets.forEach((bet) => {
            const selection = bet.selection || bet.player_name || "";
            const exposureAmount = parseFloat(bet.exposer || bet.exposure_amount || "0") || 0;
            if (selection && exposureAmount !== 0) {
              createExposureKeys(selection).forEach((key) => {
                if (key) exposureMap[key] = exposureAmount;
              });
            }
          });
          setExposures((prev) => ({ ...prev, ...exposureMap }));
        }
      }
    } catch (error) {
      console.error("Error fetching my bets:", error);
    }
  }, [matchId]);

  // Fetch exposure / my bets shortly after the round loads, then every 2 seconds
  useEffect(() => {
    if (!matchId) return;
    const timeoutId = setTimeout(() => {
      fetchExposure();
      fetchMyBetsData();
    }, 2000);
    const intervalId = setInterval(() => {
      fetchExposure();
      fetchMyBetsData();
    }, 2000);
    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [fetchExposure, fetchMyBetsData, matchId]);

  // Admin panel places no bets (PlaceBet is the NoBet stub) — an odds click refreshes the book.
  const handleBetClick = (value) => {
    if (!value) return;
    fetchExposure();
    fetchMyBetsData();
  };

  const d = gameData?.data?.data;
  const sub = Array.isArray(d?.sub) ? d.sub : [];
  const main = sub.filter((s) => s?.subtype === gameType).sort(bySr);
  const underover = sub.filter((s) => s?.subtype === "uo").sort(bySr);

  return (
    <CasinoTableLayout tableClass="teenpatti2" rightSide={<MyBet bets={myBets} formatNation={formatTeen6Nation} onViewMore={() => setShowViewMoreModal(true)} />}>
      <>
        <CasinoHeading name={title} roundId={d?.mid ?? ""} />
        <VideoBox
          iframeSrc={iframeSrc}
          timerValue={d?.lt}
          gameType={gameType}
          cardsOverlay={<Teen20RefCardsOverlay cards={d?.card} showNames={false} />}
        />
        {d?.mid ? (
          <div className="casino-detail">
            <BetTableTeenPatti3
              main={main}
              underover={underover}
              onBetClick={handleBetClick}
              exposures={exposures}
              myBets={myBets}
            />{" "}
            {d?.remark ? (
              <div className="casino-remark mt-3">
                <div className="remark-icon">
                  <img src={`${import.meta.env.BASE_URL}assets/img/icons/remark.png`} alt="" />
                </div>{" "}
                <marquee>{d.remark}</marquee>
              </div>
            ) : null}{" "}
            <CasinoLastResult
              gameType={gameType}
              resultComponent={<ResultTeen41 gameType={gameType} fallbackTitle={title} roundId={d?.mid} />}
            />
          </div>
        ) : null}
      </>
      <ViewMoreModal
        show={showViewMoreModal}
        onHide={() => setShowViewMoreModal(false)}
        title="View More"
        sectionKey={gameType}
        formatNation={formatTeen6Nation}
      />
    </CasinoTableLayout>
  );
}
